import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../firebase/AuthContext';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import { FaShippingFast, FaTruck, FaBox } from 'react-icons/fa';
import { getProductById } from '../../data/products';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutContainer = styled.div`
  max-width: 1000px;
  margin: 2rem auto;
  padding: 1.5rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const CheckoutHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`;

const Title = styled.h2`
  color: #333;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #666;
  margin: 0;
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckoutSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const ShippingOptionsContainer = styled.div`
  margin-top: 1rem;
`;

const ShippingOption = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid ${props => props.selected ? '#C41E3A' : '#ddd'};
  border-radius: 4px;
  margin-bottom: 0.75rem;
  cursor: pointer;
  background-color: ${props => props.selected ? 'rgba(196, 30, 58, 0.05)' : 'white'};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.selected ? '#C41E3A' : '#aaa'};
  }
`;

const ShippingOptionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.selected ? '#C41E3A' : '#f0f0f0'};
  color: ${props => props.selected ? 'white' : '#666'};
  margin-right: 1rem;
  transition: all 0.2s;
`;

const ShippingOptionInfo = styled.div`
  flex: 1;
`;

const ShippingOptionName = styled.div`
  font-weight: 600;
  color: #333;
`;

const ShippingOptionDescription = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.25rem;
`;

const ShippingOptionPrice = styled.div`
  font-weight: 600;
  color: ${props => props.selected ? '#C41E3A' : '#333'};
`;

const RadioInput = styled.input`
  margin-right: 0.5rem;
`;

const OrderSummary = styled.div`
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  
  &.total {
    font-weight: bold;
    font-size: 1.1rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid #ddd;
  }
`;

const ItemsList = styled.div`
  margin-bottom: 1.5rem;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  
  .item-name {
    flex: 1;
  }
  
  .item-qty {
    width: 50px;
    text-align: center;
  }
  
  .item-price {
    width: 80px;
    text-align: right;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: ${props => props.primary ? 'none' : '1px solid #C41E3A'};
  border-radius: 4px;
  background-color: ${props => props.primary ? '#C41E3A' : 'white'};
  color: ${props => props.primary ? 'white' : '#C41E3A'};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.primary ? '#A01930' : '#f8f8f8'};
  }
  
  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #C41E3A;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(196, 30, 58, 0.1);
  border-radius: 4px;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(40, 167, 69, 0.1);
  border-radius: 4px;
`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, clearCart, getCartTotals } = useCart();
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: currentUser?.displayName?.split(' ')[0] || '',
    lastName: currentUser?.displayName?.split(' ').slice(1).join(' ') || '',
    email: currentUser?.email || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedShippingOption, setSelectedShippingOption] = useState('standard');
  
  // Define shipping options
  const shippingOptions = [
    {
      id: 'economy',
      name: 'Economy Shipping',
      description: 'Delivery in 5-7 business days',
      price: 3.99,
      minDays: 5,
      maxDays: 7,
      icon: <FaBox />
    },
    {
      id: 'standard',
      name: 'Standard Shipping',
      description: 'Delivery in 3-5 business days',
      price: 5.99,
      minDays: 3,
      maxDays: 5,
      icon: <FaTruck />
    },
    {
      id: 'express',
      name: 'Express Shipping',
      description: 'Delivery in 1-2 business days',
      price: 12.99,
      minDays: 1,
      maxDays: 2,
      icon: <FaShippingFast />
    }
  ];
  
  const { subtotal, itemCount } = getCartTotals();
  
  // Get selected shipping option details
  const selectedShipping = shippingOptions.find(option => option.id === selectedShippingOption) || shippingOptions[1];
  const shipping = subtotal > 0 ? selectedShipping.price : 0;
  
  const tax = subtotal * 0.07; // 7% tax
  const total = subtotal + shipping + tax;
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleShippingOptionChange = (optionId) => {
    setSelectedShippingOption(optionId);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Validate form
      if (!formData.firstName || !formData.lastName || !formData.email || 
          !formData.address || !formData.city || !formData.state || 
          !formData.zipCode || !formData.country) {
        throw new Error('Please fill in all required fields');
      }
      
      // Validate stock availability for all items in cart
      const stockErrors = [];
      for (const item of cart) {
        const product = getProductById(item.id);
        const availableStock = product?.stock || 0;
        
        if (item.quantity > availableStock) {
          stockErrors.push(`${item.name}: Only ${availableStock} available (you have ${item.quantity} in cart)`);
        }
      }
      
      if (stockErrors.length > 0) {
        throw new Error(`Stock availability issue:\n${stockErrors.join('\n')}`);
      }
      
      // Sanitize cart items to ensure Firestore compatibility
      const sanitizedCartItems = cart.map(item => ({
        id: item.id || '',
        name: item.name || '',
        price: Number(item.price) || 0,
        quantity: Number(item.quantity) || 0,
        imageUrl: item.imageUrl || '',
        description: item.description || ''
      }));

      // Sanitize shipping option
      const sanitizedShipping = {
        id: selectedShipping?.id || 'standard',
        label: selectedShipping?.label || 'Standard Shipping',
        price: Number(selectedShipping?.price) || 0
      };

      // Create order object
      const orderData = {
        customerInfo: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
          userId: currentUser?.uid || null,
        },
        orderDetails: {
          items: sanitizedCartItems,
          subtotal: Number(subtotal) || 0,
          shipping: Number(shipping) || 0,
          shippingOption: sanitizedShipping,
          tax: Number(tax) || 0,
          total: Number(total) || 0,
          itemCount: Number(itemCount) || 0,
        },
        status: 'awaiting_payment',
        paymentStatus: 'unpaid',
        createdAt: serverTimestamp(),
      };
      
      // Save order to Firestore
      const orderRef = await addDoc(collection(db, 'orders'), orderData);
      console.log('Order saved with ID:', orderRef.id);
      
      // Note: Order confirmation email will be sent AFTER payment is confirmed
      // in the OrderConfirmation component
      
      // Store order ID in localStorage for retrieval after payment
      localStorage.setItem('pendingOrderId', orderRef.id);
      
      // Store checkout information in localStorage as backup
      localStorage.setItem('checkoutInfo', JSON.stringify({
        orderId: orderRef.id,
        customerInfo: orderData.customerInfo,
        orderDetails: orderData.orderDetails,
        timestamp: new Date().toISOString(),
      }));
      
      // Initialize Stripe
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }
      
      try {
        console.log('Attempting to fetch from Netlify Function...');
        const response = await fetch('https://benevolent-sfogliatella-83141e.netlify.app/api/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerEmail: formData.email,
            lineItems: cart.map(item => ({
              price_data: {
                currency: 'usd',
                product_data: {
                  name: item.name,
                  // Ensure description is never empty
                  description: item.description || `${item.name} - Master Collectors Forge`,
                  images: item.imageUrl ? [item.imageUrl] : [],
                },
                unit_amount: Math.round(item.price * 100), // Convert to cents
              },
              quantity: item.quantity,
            })),
            shippingOptions: [selectedShipping],
            taxAmount: Math.round(tax * 100),
            successUrl: `${window.location.origin}/order-confirmation?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/cart`,
            metadata: {
              orderId: orderRef.id,
              userId: currentUser?.uid || null,
              customerName: `${formData.firstName} ${formData.lastName}`,
              shippingAddress: `${formData.address}, ${formData.city}, ${formData.state}, ${formData.zipCode}, ${formData.country}`,
              shippingOption: selectedShipping.id
            }
          }),
        });
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          try {
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.error || 'Failed to create checkout session');
          } catch (e) {
            throw new Error(`Failed to create checkout session: ${errorText}`);
          }
        }
        
        const session = await response.json();
        console.log('Session created:', session.id);
        
        // Redirect to Stripe Checkout
        const result = await stripe.redirectToCheckout({
          sessionId: session.id,
        });
        
        if (result.error) {
          throw new Error(result.error.message);
        }
      } catch (error) {
        console.error('Checkout error:', error);
        setError(error.message || 'An error occurred during checkout');
        setLoading(false);
        return; // Stop execution to prevent further errors
      }
      
      // Success - this won't execute immediately as the user is redirected
      setSuccess('Redirecting to payment page...');
      
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message || 'An error occurred during checkout');
      setLoading(false);
    }
  };
  
  if (cart.length === 0 && !success) {
    navigate('/cart');
    return null;
  }
  
  return (
    <CheckoutContainer>
      <CheckoutHeader>
        <Title>Checkout</Title>
        <Subtitle>Complete your order by providing your shipping details</Subtitle>
      </CheckoutHeader>
      
      <form onSubmit={handleSubmit}>
        <CheckoutGrid>
          <div>
            <CheckoutSection>
              <SectionTitle>Contact Information</SectionTitle>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </CheckoutSection>
            
            <CheckoutSection>
              <SectionTitle>Shipping Address</SectionTitle>
              <FormGroup>
                <Label htmlFor="address">Street Address</Label>
                <Input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="city">City</Label>
                <Input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="state">State/Province</Label>
                <Input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="zipCode">ZIP/Postal Code</Label>
                <Input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="country">Country</Label>
                <Select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="AU">Australia</option>
                </Select>
              </FormGroup>
            </CheckoutSection>
            
            <CheckoutSection>
              <SectionTitle>Shipping Options</SectionTitle>
              <ShippingOptionsContainer>
                {shippingOptions.map(option => (
                  <ShippingOption 
                    key={option.id}
                    selected={selectedShippingOption === option.id}
                    onClick={() => handleShippingOptionChange(option.id)}
                  >
                    <ShippingOptionIcon selected={selectedShippingOption === option.id}>
                      {option.icon}
                    </ShippingOptionIcon>
                    <ShippingOptionInfo>
                      <ShippingOptionName>{option.name}</ShippingOptionName>
                      <ShippingOptionDescription>{option.description}</ShippingOptionDescription>
                    </ShippingOptionInfo>
                    <ShippingOptionPrice selected={selectedShippingOption === option.id}>
                      {formatCurrency(option.price)}
                    </ShippingOptionPrice>
                    <RadioInput 
                      type="radio"
                      name="shippingOption"
                      value={option.id}
                      checked={selectedShippingOption === option.id}
                      onChange={() => handleShippingOptionChange(option.id)}
                    />
                  </ShippingOption>
                ))}
              </ShippingOptionsContainer>
            </CheckoutSection>
          </div>
          
          <div>
            <CheckoutSection>
              <SectionTitle>Order Summary</SectionTitle>
              <OrderSummary>
                <ItemsList>
                  {cart.map(item => (
                    <Item key={item.id}>
                      <span className="item-name">{item.name}</span>
                      <span className="item-qty">x{item.quantity}</span>
                      <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                    </Item>
                  ))}
                </ItemsList>
                
                <SummaryRow>
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>Shipping</span>
                  <span>{formatCurrency(shipping)}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>Tax (7%)</span>
                  <span>{formatCurrency(tax)}</span>
                </SummaryRow>
                <SummaryRow className="total">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </SummaryRow>
              </OrderSummary>
            </CheckoutSection>
          </div>
        </CheckoutGrid>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <ButtonGroup>
          <Button type="button" onClick={() => navigate('/cart')}>
            Back to Cart
          </Button>
          <Button type="submit" primary disabled={loading}>
            {loading ? 'Processing...' : 'Proceed to Payment'}
          </Button>
        </ButtonGroup>
      </form>
    </CheckoutContainer>
  );
};

export default Checkout;
